(() => {
  'use strict';

  if (window.__foodnetCartBridgeV77) return;
  window.__foodnetCartBridgeV77 = true;

  const TOAST_DURATION_MS = 3600;
  const TOAST_EXIT_MS = 340;
  let toastTimer = 0;

  const rootPath = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };
  const route = (path) => `${rootPath()}${String(path).replace(/^\//, '')}`;
  const formatMoney = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'nl-BE', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'EUR'
  }).format((Number(cents) || 0) / 100);

  const syncHeaderCart = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count || 0);
      count.hidden = !cart.item_count;
      count.classList.remove('fn-cart-count--updated');
      window.requestAnimationFrame(() => {
        count.classList.add('fn-cart-count--updated');
        window.setTimeout(() => count.classList.remove('fn-cart-count--updated'), 760);
      });
    });
    document.querySelectorAll('[data-header-cart-total]').forEach((total) => {
      total.textContent = formatMoney(cart.total_price);
    });
  };

  const initForm = (form) => {
    if (form.dataset.foodnetCartBridgeReady === 'true') return;
    form.dataset.foodnetCartBridgeReady = 'true';

    const productRoot = form.closest('[data-product-root]') || document;
    const variantInput = form.querySelector('[data-variant-select], input[name="id"]');
    const variantJson = form.querySelector('[data-variants-json]');
    const status = form.parentElement?.querySelector('[data-product-cart-status]');
    const submit = form.querySelector('.product-submit, button[type="submit"]');
    let variants = [];
    let busy = false;

    try {
      variants = JSON.parse(variantJson?.textContent || '[]');
    } catch (_) {
      variants = [];
    }

    const clearStatus = () => {
      if (!status) return;
      status.replaceChildren();
      status.dataset.state = '';
    };

    const hideStatus = () => {
      if (!status || !status.childElementCount) return;
      status.dataset.state = 'leaving';
      toastTimer = window.setTimeout(clearStatus, TOAST_EXIT_MS);
    };

    const showStatus = (message, state = '') => {
      if (!status) return;
      window.clearTimeout(toastTimer);
      clearStatus();
      if (!message) return;

      const icon = document.createElement('span');
      icon.className = 'fn-cart-feedback__icon';
      icon.setAttribute('aria-hidden', 'true');
      const mark = document.createElement('span');
      mark.className = 'fn-cart-feedback__mark';
      mark.textContent = state === 'success' ? '✓' : '!';
      icon.append(mark);

      const copy = document.createElement('span');
      copy.className = 'fn-cart-feedback__copy';
      const title = document.createElement('strong');
      title.className = 'fn-cart-feedback__title';
      title.textContent = state === 'success' ? 'Toegevoegd' : 'Toevoegen mislukt';
      const detail = document.createElement('span');
      detail.className = 'fn-cart-feedback__detail';
      detail.textContent = state === 'success' ? 'Het product staat in je winkelwagen.' : message;
      copy.append(title, detail);

      const flourish = document.createElement('span');
      flourish.className = 'fn-cart-feedback__flourish';
      flourish.setAttribute('aria-hidden', 'true');
      for (let index = 0; index < 4; index += 1) flourish.append(document.createElement('span'));
      const progress = document.createElement('span');
      progress.className = 'fn-cart-feedback__progress';
      progress.setAttribute('aria-hidden', 'true');

      status.append(icon, copy, flourish, progress);
      status.style.setProperty('--fn-toast-duration', `${TOAST_DURATION_MS}ms`);
      status.dataset.state = state;
      if (state === 'success') toastTimer = window.setTimeout(hideStatus, TOAST_DURATION_MS);
    };

    status?.setAttribute('aria-atomic', 'true');

    const selectedVariant = () => {
      if (!variants.length) return null;
      const optionInputs = [...form.querySelectorAll('[data-option-value]')];
      if (!optionInputs.length) {
        return variants.find((variant) => String(variant.id) === String(variantInput?.value)) || variants[0];
      }
      const groups = [...new Set(optionInputs.map((input) => input.name))];
      const chosen = groups.map((name) => form.querySelector(`[name="${CSS.escape(name)}"]:checked`)?.value);
      if (chosen.some((value) => value == null)) return null;
      return variants.find((variant) => (variant.options || []).every((option, index) =>
        String(option) === String(chosen[index])
      )) || null;
    };

    const announceVariant = (variant) => {
      window.dispatchEvent(new CustomEvent('foodnet:variant-change', {
        detail: { form, productRoot, variant }
      }));
    };

    const syncVariant = ({ updateUrl = true, announce = true } = {}) => {
      const variant = selectedVariant();
      if (!variant || !variantInput) return null;

      variantInput.value = String(variant.id);
      const available = Boolean(variant.available);
      if (submit) {
        submit.disabled = !available || busy;
        if (!busy) {
          submit.textContent = available
            ? (submit.dataset.addLabel || 'In winkelwagen')
            : (submit.dataset.soldLabel || 'Niet beschikbaar');
        }
      }

      const skuWrap = productRoot.querySelector('[data-product-sku-wrap]');
      const sku = productRoot.querySelector('[data-product-sku]');
      if (skuWrap && sku) {
        sku.textContent = variant.sku || '';
        skuWrap.hidden = !variant.sku;
      }

      const stock = productRoot.querySelector('[data-product-stock]');
      const stockLabel = productRoot.querySelector('[data-product-stock-label]');
      const dot = productRoot.querySelector('[data-stock-dot]');
      if (stock && stockLabel) {
        stockLabel.textContent = available ? stock.dataset.inLabel : stock.dataset.outLabel;
      }
      if (dot) dot.style.background = available ? '#62a52c' : '#b8bdb8';

      const featured = variant.featured_image?.src || variant.featured_image?.url;
      const mainImage = productRoot.querySelector('.product-main-gallery-image');
      if (featured && mainImage) {
        mainImage.src = featured;
        mainImage.srcset = '';
      }

      const sticky = document.querySelector('[data-sticky-atc]');
      if (sticky) {
        const stickyId = sticky.querySelector('[data-sticky-variant-id]');
        const stickyButton = sticky.querySelector('[type="submit"]');
        if (stickyId) stickyId.value = String(variant.id);
        if (stickyButton) {
          stickyButton.disabled = !available;
          stickyButton.textContent = available
            ? (stickyButton.dataset.addLabel || 'In winkelwagen')
            : (stickyButton.dataset.soldLabel || 'Niet beschikbaar');
        }
      }

      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      if (announce) announceVariant(variant);
      return variant;
    };

    /*
      Canonical variant controller for FoodNet PDP.
      Capture phase prevents the legacy WOKORA product listener from also writing
      raw prices/SKU/stock after this controller has selected the variant.
    */
    form.addEventListener('change', (event) => {
      if (!event.target.matches('[data-option-value]')) return;
      event.stopImmediatePropagation();
      syncVariant({ updateUrl: true, announce: true });
    }, true);

    syncVariant({ updateUrl: false, announce: true });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (busy) return;

      const variant = syncVariant({ updateUrl: true, announce: false });
      if (!variant || !variantInput?.value) {
        showStatus('Kies eerst een verpakking.', 'error');
        return;
      }
      if (!variant.available) {
        showStatus('Deze verpakking is momenteel niet beschikbaar.', 'error');
        return;
      }

      busy = true;
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        submit.textContent = 'Toevoegen…';
      }
      showStatus('');

      try {
        const addResponse = await fetch(route('cart/add.js'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        const addData = await addResponse.json().catch(() => ({}));
        if (!addResponse.ok) {
          throw new Error(addData.description || addData.message || 'Dit product kon niet worden toegevoegd.');
        }

        try {
          const cartResponse = await fetch(route('cart.js'), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            cache: 'no-store'
          });
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            syncHeaderCart(cart);
            document.dispatchEvent(new CustomEvent('wokora:cart-updated', { detail: { cart, source: 'foodnet-product' } }));
          }
        } catch (_) {}

        showStatus('Toegevoegd aan je winkelwagen.', 'success');
      } catch (error) {
        showStatus(error?.message || 'Toevoegen mislukt. Probeer opnieuw.', 'error');
      } finally {
        busy = false;
        const current = syncVariant({ updateUrl: false, announce: true });
        if (submit) {
          submit.removeAttribute('aria-busy');
          submit.disabled = !current?.available;
          submit.textContent = current?.available
            ? (submit.dataset.addLabel || 'In winkelwagen')
            : (submit.dataset.soldLabel || 'Niet beschikbaar');
        }
      }
    }, true);
  };

  const boot = () => {
    document.querySelectorAll('[data-product-form][data-foodnet-cart-bridge]').forEach(initForm);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
